import { io } from 'socket.io-client';
import { normalizeBaseUrl } from './config';
export function createSkyChatSocket(baseUrl, token, handlers = {}, options = {}) {
    const socket = io(normalizeBaseUrl(baseUrl), {
        autoConnect: false,
        path: '/socket.io',
        // Keep the main chat transport identical to the Web client. In deployments with
        // multiple upstreams, a polling handshake can land on a different process from the
        // upgrade and leave the React app connected-looking but unable to deliver events.
        transports: ['websocket']
    });
    const authPayload = (nextToken) => ({
        token: nextToken,
        ...(options.deviceKind ? { deviceKind: options.deviceKind } : {}),
        ...(options.deviceSessionId ? { deviceSessionId: options.deviceSessionId } : {})
    });
    socket.on('connect', () => {
        if (token)
            socket.emit('auth', authPayload(token));
    });
    socket.on('message:receive', (message) => handlers.onMessage?.(message));
    socket.on('message:sync', (message) => handlers.onSync?.(message));
    socket.on('message:ack', (payload) => handlers.onAck?.(payload));
    socket.on('message:ack:v2', (payload) => handlers.onAck?.(payload));
    socket.on('message:error', (payload) => handlers.onError?.(payload || {}));
    socket.on('message:status', (payload) => handlers.onStatus?.(payload));
    socket.on('read_status:sync', (payload) => handlers.onReadStatusSync?.(payload));
    socket.on('chats:sync_all_read_statuses', (payload) => handlers.onAllReadStatusesSync?.(payload));
    socket.on('typing', (payload) => handlers.onTyping?.(payload));
    socket.on('friends:refresh', () => handlers.onFriendsRefresh?.());
    socket.on('contact-settings:update', (payload) => handlers.onContactSettingsUpdate?.(payload || {}));
    ['group:added', 'group:update', 'group:removed', 'group:role-changed'].forEach((event) => {
        socket.on(event, (payload) => handlers.onGroupChange?.(event, payload));
    });
    ['game:invite', 'game:update', 'game:party:invite', 'game:party:invite:response', 'game:session:progress', 'game:session:chat'].forEach((event) => {
        socket.on(event, (payload) => handlers.onGameEvent?.(event, payload));
    });
    ['whiteboard:draw', 'whiteboard:clear', 'whiteboard:laser', 'whiteboard:text', 'whiteboard:board:state', 'whiteboard:cursor'].forEach((event) => {
        socket.on(event, (payload) => handlers.onWhiteboardEvent?.(event, payload));
    });
    [
        'meeting:invite',
        'meeting:updated',
        'meeting:participant-updated',
        'meeting:ended',
        'meeting:cancelled',
        'meeting:peer-ready',
        'meeting:signal',
        'meeting:screen-state'
    ].forEach((event) => {
        socket.on(event, (payload) => handlers.onScreenShareEvent?.(event, payload));
    });
    [
        'device-transfer:devices:update',
        'device-transfer:request:incoming',
        'device-transfer:request:accepted',
        'device-transfer:request:rejected',
        'device-transfer:request:cancelled',
        'device-transfer:webrtc:offer',
        'device-transfer:webrtc:answer',
        'device-transfer:webrtc:ice',
        'device-transfer:progress',
        'device-transfer:done',
        'device-transfer:error',
        'device-transfer:lan-host:start-request',
        'device-transfer:lan-hosts:update',
        'device-transfer:lan-host:update',
        'device-transfer:lan-probe:authorization',
        'device-transfer:lan-common-hosts:update',
        'device-transfer:lan-session:incoming',
        'device-transfer:lan-session:authorized',
        'device-transfer:lan-session:key-envelope',
        'device-transfer:lan-session:status',
        'device-transfer:lan-session:cancelled'
    ].forEach((event) => {
        socket.on(event, (payload) => handlers.onDeviceTransferEvent?.(event, payload));
    });
    return {
        socket,
        connect: () => socket.connect(),
        disconnect: () => socket.disconnect(),
        authenticate: (nextToken) => socket.emit('auth', authPayload(nextToken)),
        sendMessage: (message) => socket.emit('message:send', message),
        markRead: (peerId, lastTs) => socket.emit('chat:mark_read', { peerId, lastTs }),
        emit: (event, payload, ack) => socket.emit(event, payload, ack)
    };
}
//# sourceMappingURL=socket.js.map